// Most code was extracted and adapted from the classic experience implemented in
// app/assets/modules/github/issues/convert-to-issue-button.ts

import type {OnDropArgs} from '@github-ui/drag-and-drop'
import {listItemToString, parseListItem, type ListItem} from '@github-ui/markdown-editor/list-editing'
import {useLayoutEffect} from '@github-ui/use-layout-effect'
import {useMemo, useState} from 'react'

const TASKLIST_SELECTOR = '.contains-task-list'
const TASKLIST_ITEM_SELECTOR = 'li.task-list-item'
const REFERENCE_CLASSNAME = 'reference'

export type TaskItem = {
  /**
   * Sanitized task name
   */
  title: string
  /**
   * Position of the task in the list `[listNumber, itemIndex]`
   */
  position: [number, number]

  id: string
  index: number
  content: string
  checked: boolean
  container: Element
  nested: boolean
  children: TaskItem[]
  markdownIndex: string
}

export type TaskListItem = ListItem & {taskBox: '[ ]' | '[x]'}

export type onDropProps = OnDropArgs<string | number> & {
  items: TaskItem[]
  container: Element
}

/**
 * Rejects inputs (task's checkbox) or nested lists
 */
function isContentNode(node: Node) {
  return node.nodeName !== 'INPUT' && node.nodeName !== 'UL' && node.nodeName !== 'OL'
}

/**
 * Search for children with the `reference` class.
 *
 * Example of issue reference element:
 *
 * ```
 * <span class="reference">
 *  <svg class="octicon" />
 *  <a class="issue-link js-issue-link" href="URL">Issue title<span class="issue-shorthand">&nbsp;#123</span></a>
 * </span>
 * ````
 */
function hasIssueReference(element: HTMLElement) {
  for (const node of element.children) {
    // Ignore checking within the nested lists
    if (!isContentNode(node)) continue

    // Check for node's class list and in nested elements
    const currentElementIsAReference = node.classList.contains(REFERENCE_CLASSNAME)
    const nestedElementsHaveAReference = node.querySelector(`.${REFERENCE_CLASSNAME}`)

    if (currentElementIsAReference || nestedElementsHaveAReference) return true
  }

  return false
}

/**
 * Gets the text content of all children nodes of the item, excluding the input and nested lists.
 *
 * `textContent` is used to strip all the HTML with the exception of `<code>` tags, which we replace for backticks.
 */
export function getTaskItemContent(element: Element) {
  let name = ''
  let content = ''

  for (const child of element.childNodes) {
    if (!isContentNode(child)) continue

    if (child.nodeName === '#text') {
      name += child.textContent
      content += child.textContent
      continue
    }

    if (child.nodeName === 'CODE') {
      name += `\`${child.textContent}\``
      content += (child as HTMLElement).outerHTML
      continue
    }

    name += child.textContent
    content += (child as HTMLElement).outerHTML
  }

  return {
    title: name.trim(),
    content,
  }
}

/**
 * Calculate the absolute index for a list.
 *
 * Indentation is not relevant but rather the order by which we traverse the items.
 */
function getListIndex(container: Element, element: Element) {
  const lists = Array.from(container.querySelectorAll(`ul, ol`)).filter(item => {
    // Filter out tasklist blocks
    return !item.closest('tracking-block')
  })

  return lists.indexOf(element)
}
/**
 * Calculate the list index and item index for a task item.
 *
 * Item index is the position of the task within the list, including regular list items that are not tasks.
 */
export function getTaskPosition(container: Element, element: Element): [number, number] {
  const taskList = element.parentElement?.closest(TASKLIST_SELECTOR)
  if (!taskList) throw new Error("Could not find the item's parent list")

  const index = element ? Array.from(taskList.children).indexOf(element) : -1
  return [getListIndex(container, taskList), index]
}

/**
 * Tasklist item parser.
 *
 * Parses the sanitized task item names and calculates their position in the list to trigger conversions.
 */
export function getTasklistItems(container: Element): TaskItem[] {
  const mappedItems: TaskItem[] = []
  const items = Array.from(container.querySelectorAll<HTMLLIElement>(TASKLIST_ITEM_SELECTOR) ?? [])

  for (const item of items) {
    if (hasIssueReference(item)) continue

    const {title, content} = getTaskItemContent(item)
    const position = getTaskPosition(container, item)

    mappedItems.push({
      id: '0',
      index: 0,
      title,
      content,
      position,
      checked: false,
      children: [],
      container,
      nested: false,
      markdownIndex: '0',
    })
  }

  return mappedItems
}

export function useTasklist(container: Element, dependencies?: unknown[]) {
  return useMemo(() => {
    if (!container) return []

    return getTasklistItems(container)

    // We want to enable consumers to pass their own depencencies so they can re-trigger it
    // without depending on the container ref changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container, dependencies])
}

export const isTaskListItem = (item: ListItem | null): item is TaskListItem => typeof item?.taskBox === 'string'

/**
 * Parses the beginning of a code fence in a Markdown file.
 *
 * @param line The line of text to parse.
 * @returns The code fence characters if found, otherwise null.
 */
export const parseCodeFenceBegin = (line: string) => {
  const match = line.match(/^ {0,3}(`{3,}|~{3,})[^`]*$/)
  return match ? match[1] : null
}

/**
 * Checks if a line is the end of a code fence.
 * @param line - The line to check.
 * @param fence - The code fence string.
 * @returns A boolean indicating whether the line is the end of a code fence.
 */
export const isCodeFenceEnd = (line: string, fence: string) => {
  const regex = new RegExp(`^ {0,3}${fence}${fence[0]}* *$`)
  return regex.test(line)
}

/**
 * Maps the task list items recursively and returns an array of items that can be used by the Drag and Drop component.
 *
 * @param {Object} options - The options object.
 * @param {Element} options.container - The container element.
 * @param {Map<string, number>} options.nestedItems - The map of nested items.
 * @param {number} options.globalIndex - The global index.
 * @param {Element[]} options.items - The array of items.
 * @param {TaskItem[]} options.itemsData - The array to store the task item data.
 * @param {boolean} options.nested - Indicates if the items are nested.
 */
const mapTaskListItems = ({
  container,
  nestedItems,
  globalIndex,
  items,
  itemsData,
  nested = false,
  markdownIndex,
}: {
  container: Element
  nestedItems: Map<string, number>
  globalIndex: number
  items: Element[]
  itemsData: TaskItem[]
  nested: boolean
  markdownIndex: number
}) => {
  items.map(el => {
    const children = Array.from(el.querySelectorAll(':scope > ul.contains-task-list > li.task-list-item')) ?? []
    let totalChildren = children.length
    children.length && calculateChildren(children)
    const mappedChildren: TaskItem[] = []

    // calculate the total number of nested items in a list item
    function calculateChildren(nestedChildren: Element[]) {
      nestedChildren.map(item => {
        const nestedElement =
          Array.from(item.querySelectorAll(':scope > ul.contains-task-list > li.task-list-item')) ?? []
        totalChildren += nestedElement.length
        return nestedElement.length > 0 && calculateChildren(nestedElement)
      })
    }

    !nested && nestedItems.set(markdownIndex.toString(), totalChildren)
    const position = getTaskPosition(container, el)
    const {title, content} = getTaskItemContent(el)

    if (totalChildren > 0) {
      mapTaskListItems({
        container,
        nestedItems,
        globalIndex,
        items: children,
        itemsData: mappedChildren,
        nested: true,
        markdownIndex,
      })
    }

    itemsData.push({
      id: globalIndex.toString(),
      index: globalIndex++,
      title,
      content,
      checked: (el.querySelector('.task-list-item-checkbox') as HTMLInputElement)?.checked === true,
      position,
      container,
      nested,
      children: mappedChildren,
      markdownIndex: markdownIndex.toString(),
    })

    markdownIndex = markdownIndex + (nestedItems.get(markdownIndex.toString()) ?? 0) + 1
  })

  return {markdownIndex}
}

/**
 * Retrieves data from html tasklists, gets corresponding data to use in Drag and Drop Component from them,
 * and clears them from the DOM.
 *
 * @param {Object} options - The options object.
 * @param {Element} options.container - The container element.
 * @param {Element[]} options.tasklists - An array of tasklist elements.
 * @param {React.Dispatch<React.SetStateAction<Map<Element, TaskItem[]>>>} options.setTasklistData - The state setter for tasklist data.
 * @param {React.Dispatch<React.SetStateAction<Map<string, number>>>} options.setNestedItems - The state setter for nested items.
 */
export function getDataAndClearTasklists({
  container,
  tasklists,
  setTasklistData,
  setNestedItems,
}: {
  container: Element
  tasklists: Element[]
  setTasklistData: React.Dispatch<React.SetStateAction<Map<Element, TaskItem[]>>>
  setNestedItems: React.Dispatch<React.SetStateAction<Map<string, number>>>
}) {
  const newData = new Map<Element, TaskItem[]>()
  const nestedItems = new Map<string, number>()

  const globalIndex = 0
  let markdownIndex = 0
  for (const tasklist of tasklists) {
    const items = Array.from(tasklist.querySelectorAll(':scope > li.task-list-item'))
    const itemsData: TaskItem[] = []
    const {markdownIndex: newMarkdownIndex} = mapTaskListItems({
      container,
      nestedItems,
      globalIndex,
      items,
      itemsData,
      nested: false,
      markdownIndex,
    })
    markdownIndex = newMarkdownIndex
    newData.set(tasklist, itemsData)
    for (const item of items) item.remove()
  }

  setTasklistData(newData)
  setNestedItems(nestedItems)
  return null
}

/**
 * Checks if two arrays are equal.
 * @param a - The first array.
 * @param b - The second array.
 * @returns True if the arrays are equal, false otherwise.
 */
function arraysEqual(a: unknown[], b: unknown[]) {
  if (a.length !== b.length) return false

  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false

  return true
}

/**
 * Returns an array of elements that match the specified selector within a container.
 *
 * @param containerRef - A React ref object that references the container element.
 * @param selector - A CSS selector string used to select elements within the container.
 * @returns An array of elements that match the selector.
 */
export function useQuerySelectorAll(containerRef: React.RefObject<Element>, selector: string) {
  const [elements, setElements] = useState<Element[]>([])

  // We do want this to run on every render. It will only trigger a rerender if the elements have changed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const newElements = Array.from(containerRef.current?.querySelectorAll(`:scope > ${selector}`) ?? [])
    // careful to avoid infinite rerendering loop
    // this works because the tasklist containers are preserved when we insert the tasklist
    setElements(oldElements => (arraysEqual(oldElements, newElements) ? oldElements : newElements))
  })

  return elements
}

/**
 * Returns the line index of a specific task list item in a markdown string.
 *
 * @param taskListIndex - The index of the task list item to find.
 * @param markdownValue - The markdown string to search in.
 * @returns The line index of the task list item, or -1 if not found.
 */
export const getLineInMarkdown = (taskListIndex: number, markdownValue: string) => {
  const lines = markdownValue.split(/\r?\n/)
  for (let lineIndex = 0, taskIndex = 0; lineIndex < lines.length; lineIndex++) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const line = lines[lineIndex]!
    const parsedLine = parseListItem(line)

    if (!isTaskListItem(parsedLine)) continue

    if (taskIndex === taskListIndex) {
      return lineIndex
    }

    taskIndex++
  }

  return -1
}

/**
 * Updates the markdown value by moving a line and its nested items to a new position.
 *
 * @param markdownValue - The original markdown value.
 * @param dragLineIndex - The index of the line being dragged.
 * @param isBefore - Indicates whether the line is being dropped before or after the target line.
 * @param dropLineIndex - The index of the target line where the dragged line will be dropped.
 * @param draggedChildren - The number of nested items being dragged along with the line.
 * @returns The updated markdown value.
 */
export const updateMarkdown = (
  markdownValue: string,
  dragLineIndex: number,
  isBefore: boolean,
  dropLineIndex: number,
  draggedChildren: number,
) => {
  const lines = markdownValue.split(/\r?\n/)
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const dragLine = lines[dragLineIndex]!

  if (isBefore) {
    // remove dragLine
    lines.splice(dragLineIndex, 1)

    // insert dragLine at the correct position
    lines.splice(dropLineIndex + (isBefore ? 0 : 1), 0, dragLine)

    // insert and remove nested items to markdown
    let i = 1
    while (i <= draggedChildren) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const dragL = lines[dragLineIndex + i]!
      lines.splice(dropLineIndex + i + (isBefore ? 0 : 1), 0, dragL)
      lines.splice(dragLineIndex + i + 1, 1)
      i++
    }
  } else {
    // insert dragLine at the correct position
    lines.splice(dropLineIndex + (isBefore ? 0 : 1), 0, dragLine)

    // insert nested items to markdown
    let i = 1
    while (i <= draggedChildren) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const dragL = lines[dragLineIndex + i]!
      lines.splice(dropLineIndex + i + (isBefore ? 0 : 1), 0, dragL)
      i++
    }

    // remove dragLine
    lines.splice(dragLineIndex, 1)

    // remove nested items from markdown
    let j = 1
    while (j <= draggedChildren) {
      lines.splice(dragLineIndex, 1)
      j++
    }
  }

  return lines.join('\n')
}

/**
 * Updates the local state of a task list by rearranging the items based on drag and drop.
 *
 * @param items - The array of task items.
 * @param dragId - The ID of the item being dragged.
 * @param dropId - The ID of the item where the dragged item is dropped.
 * @param tasklistData - The map containing the task list data.
 * @param isBefore - A boolean indicating whether the dragged item is dropped before or after the drop item.
 * @param container - The container element of the task list.
 * @returns The updated map of task list data.
 */
export const updateLocalState = (
  items: TaskItem[],
  dragId: string | number,
  dropId: string | number,
  tasklistData: Map<Element, TaskItem[]>,
  isBefore: boolean,
  container: Element,
): Map<Element, TaskItem[]> => {
  const newData = new Map<Element, TaskItem[]>(tasklistData)
  const dragItem = items.find(item => item.id === dragId)
  if (!dragItem) return newData

  const updatedTaskLists = items.reduce(
    (newItems, item) => {
      if (dragItem.id === item.id) return newItems

      if (item.id !== dropId) {
        newItems.push(item)
      } else if (isBefore) {
        newItems.push(dragItem, item)
      } else {
        newItems.push(item, dragItem)
      }

      return newItems
    },
    [] as typeof items,
  )

  newData.set(container, updatedTaskLists)

  return newData
}

const toggleTaskListItem = (item: TaskListItem): TaskListItem => ({
  ...item,
  taskBox: item.taskBox === '[ ]' ? '[x]' : '[ ]',
})

/**
 * Handles the toggle action for a task item in a markdown document.
 *
 * @param {Object} options - The options object.
 * @param {React.MutableRefObject<string>} options.markdownRef - The mutable ref object containing the markdown content.
 * @param {number} options.markdownIndex - The index of the task item in the markdown content.
 * @param {boolean} options.checked - The current checked state of the task item.
 * @param {(markdown: string) => void | Promise<void>} options.onChange - The callback function to be called when the markdown content changes.
 * @param {React.Dispatch<React.SetStateAction<boolean>>} options.setChecked - The state setter function for the checked state.
 */
export const handleItemToggle = ({
  markdownRef,
  markdownIndex,
  checked,
  onChange,
  setChecked,
}: {
  markdownRef: React.MutableRefObject<string>
  markdownIndex: number
  checked: boolean
  onChange: (markdown: string) => void | Promise<void>
  setChecked: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const lines = markdownRef.current.split(/\r?\n/)
  let currentCodeFence: string | null | undefined = null

  for (let lineIndex = 0, taskIndex = 0; lineIndex < lines.length; lineIndex++) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const line = lines[lineIndex]!

    if (!currentCodeFence) {
      currentCodeFence = parseCodeFenceBegin(line)
    } else if (isCodeFenceEnd(line, currentCodeFence)) {
      currentCodeFence = null
      continue
    }

    if (currentCodeFence) continue

    const parsedLine = parseListItem(line)

    if (!isTaskListItem(parsedLine)) continue

    if (taskIndex === markdownIndex) {
      const updatedLine = listItemToString(toggleTaskListItem(parsedLine))
      lines.splice(lineIndex, 1, updatedLine)

      const updatedMarkdown = lines.join('\n')
      markdownRef.current = updatedMarkdown

      setChecked(!checked)
      onChange(updatedMarkdown)
      return
    }

    taskIndex++
  }
}
